import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';

const campaignCache = new CacheManager('VueCache', 'cache', {
    maxAge: 24 * 60 * 60 * 1000,
    memoryCacheTTL: 30 * 60 * 1000,
    logCategory: 'Campaign',
    keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.campaigns.`,
});

export async function getCampaigns(forceRefresh = false) {
    if (!forceRefresh && !campaignCache.getFromMemory('all')) {
        try {
            const legacyData = await iframeDataBridge.getStorageAsync('CAMPAIGNS');
            if (legacyData) {
                log('info', 'Campaign', '📦 Seeding Vue cache from legacy storage (CAMPAIGNS)');
                await campaignCache.put('all', legacyData);
                campaignCache.setInMemory('all', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'Campaign', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    return campaignCache.fetchOrCache('all', fetchCampaignsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getCampaignOfferPrecos(campaignIds) {
    try {
        if (!iframeDataBridge.isOnline()) {
            log('warn', 'Campaign', '📴 Offline: Cannot fetch campaign offer precos');
            return [];
        }

        const response = await authenticatedFetch(getApiUrl('Core/CampaignOfferPrecos'), { method: 'GET' }, 'token');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const campaignOfferPrecos = await response.json();

        log('success', 'Campaign', `✅ Fetched ${campaignOfferPrecos.length} campaign offer precos`);
        return campaignOfferPrecos;
    } catch (error) {
        log('error', 'Campaign', `❌ Failed to fetch campaign offer precos from API: ${error.message}`);
        return [];
    }
}

async function fetchCampaignsFromAPI() {
    const response = await authenticatedFetch(getApiUrl('Core/Campaigns'), { method: 'GET' }, 'token');

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const campaigns = await response.json();
    log('success', 'Campaign', `✅ Fetched ${campaigns.length} campaigns`);
    return campaigns;
}

export async function refreshCampaigns() {
    campaignCache.clearMemory();
    await campaignCache.clear();
}
